import React, { useEffect, useState } from "react";
import { Maximize2, FileText } from "lucide-react";
import AdminModal from "components/admin/AdminModal";
import ActionButton from "components/admin/ActionButton";
import { inputClass } from "./giveawayFormUtils";

interface RulesFieldProps {
  value: string;
  /**
   * Omitted on the read-only screens — the box and the pop-up then just display
   * the text.
   */
  onChange?: (value: string) => void;
  id?: string;
  disabled?: boolean;
  error?: string;
  maxLength?: number;
}

/**
 * Contest rules: a clickable preview box that opens into a full modal popup.
 */
const RulesField: React.FC<RulesFieldProps> = ({
  value,
  onChange,
  id = "rules",
  disabled = false,
  error,
  maxLength = 2000,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [modalText, setModalText] = useState(value);
  const readOnly = !onChange || disabled;

  // Sync modal draft text whenever modal opens or value changes
  useEffect(() => {
    setModalText(value);
  }, [value, expanded]);

  const handleModalSave = () => {
    onChange?.(modalText);
    setExpanded(false);
  };

  if (readOnly) {
    return (
      <>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setExpanded(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setExpanded(true);
            }
          }}
          className={`${inputClass} cursor-pointer min-h-[50px] flex items-center justify-between gap-3 bg-gray-50 hover:bg-gray-100 hover:border-gray-300 transition-colors group p-3`}
        >
          <div className="flex items-center gap-2.5 flex-1 min-w-0 pr-2">
            <FileText className="w-4 h-4 text-lightgray-200 shrink-0" aria-hidden="true" />
            <p className="text-sm text-grease-100 truncate">
              {value || "No rules recorded for this contest."}
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(true);
            }}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-light-yellow bg-yellow-50 rounded-md hover:bg-light-yellow hover:text-white transition-colors"
          >
            <span>View Rules</span>
            <Maximize2 className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </div>

        <AdminModal
          isOpen={expanded}
          onClose={() => setExpanded(false)}
          title="Contest Rules"
          size="lg"
          showCloseButton={false}
          footer={
            <div className="flex justify-end">
              <ActionButton onClick={() => setExpanded(false)}>
                Close
              </ActionButton>
            </div>
          }
        >
          <div className="p-2 space-y-4">
            {value?.trim() ? (
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <ul className="text-sm text-grease-100 leading-relaxed space-y-2 list-disc pl-5">
                  {value
                    .split("\n")
                    .map((r) => r.replace(/^[•\-*]\s*/, "").trim())
                    .filter(Boolean)
                    .map((rule, idx) => (
                      <li key={idx}>{rule}</li>
                    ))}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-lightgray-200 text-center py-6">
                No rules recorded for this contest.
              </p>
            )}
          </div>
        </AdminModal>
      </>
    );
  }

  return (
    <>
      <div className="relative">
        <textarea
          id={id}
          rows={3}
          maxLength={maxLength}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
          placeholder="Enter contest rules, eligibility and guidelines..."
          className={`${inputClass} resize-y min-h-[80px] max-h-[220px] pr-9 ${
            error ? "border-red-500" : ""
          }`}
        />
        <button
          type="button"
          onClick={() => {
            setModalText(value);
            setExpanded(true);
          }}
          title="Open Rules in Popup"
          aria-label="Open Rules in Popup"
          className="absolute right-2 top-2 p-1 text-lightgray-200 hover:text-grease-100 transition-colors"
        >
          <Maximize2 className="w-4 h-4" aria-hidden="true" />
        </button>
        <div className="flex justify-between items-start gap-2 mt-1">
          {error && <p className="text-xs text-red-500">{error}</p>}
          <span className="text-[11px] text-gray-400 ml-auto shrink-0">
            {value.length}/{maxLength}
          </span>
        </div>
      </div>

      <AdminModal
        isOpen={expanded}
        onClose={() => setExpanded(false)}
        title="Contest Rules"
        size="lg"
        showCloseButton={false}
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="px-6 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <ActionButton onClick={handleModalSave}>
              Save Rules
            </ActionButton>
          </div>
        }
      >
        <div className="p-2 space-y-3">
          <p className="text-xs text-lightgray-200">
            Enter each rule, term, or condition on a new line.
          </p>
          <textarea
            rows={6}
            maxLength={maxLength}
            value={modalText}
            onChange={(e) => setModalText(e.target.value)}
            placeholder="Enter contest rules, eligibility and guidelines..."
            autoFocus
            className={`${inputClass} resize-y min-h-[140px] max-h-[300px]`}
          />
          <div className="flex justify-end">
            <span className="text-[11px] text-gray-400">
              {modalText.length}/{maxLength}
            </span>
          </div>
        </div>
      </AdminModal>
    </>
  );
};

export default RulesField;
